import { FormProvider, useForm } from 'react-hook-form';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
// eslint-disable-next-line camelcase
import { unstable_usePrompt, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import FormStateErrors from './FormStateErrors';
import FormErrors from './FormErrors';
import FormWarnings from './FormWarnings';
import getInValidPostFields from '../../helpers/getInValidPostFields';
import getDirtyValues from '../../helpers/getDirtyValues';

const forceEvaluateIsDirty = (fieldName, initialValues, currentValues) => {
  if (
    Object.prototype.hasOwnProperty.call(initialValues, fieldName) &&
    Object.prototype.hasOwnProperty.call(currentValues, fieldName)
  ) {
    if (
      JSON.stringify(initialValues?.[fieldName]) !==
      JSON.stringify(currentValues?.[fieldName])
    ) {
      return true;
    }
  }
  return false;
};

const FormHandler = forwardRef(
  (
    {
      errors = null,
      warnings = null,
      disableUnSavedCheck = false,
      initialValues = {},
      children,
      onSubmit,
      validationSchema = null,
      sendDirtyOnly = false,
      formSchema,
      onReadyRenderFormItems = () => [],
      isSearchForm = false,
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const methods = useForm({
      defaultValues: initialValues,
      ...(validationSchema && { resolver: yupResolver(validationSchema) }),
      context: initialValues,
    });

    const onLocalSubmit = (data, additional) => {
      if (sendDirtyOnly) {
        // Get Dirty Values is a custom function that does not accoutn for deeply nested objects
        const forceEvaluateFields = [
          'customerServiceRequests',
          'customerPlaceId',
        ];
        let { dirtyFields } = methods.formState;
        forceEvaluateFields.forEach(item => {
          const isDirty = forceEvaluateIsDirty(item, initialValues, data);
          if (isDirty) {
            dirtyFields = {
              ...dirtyFields,
              [item]: true,
            };
            /* add customerPlaceSessionToken in request only if customerPlaceId is dirty */
            if (item === 'customerPlaceId' && isDirty) {
              dirtyFields = {
                ...dirtyFields,
                customerPlaceSessionToken: isDirty,
              };
            }
          }
        });
        const dirtyData = getDirtyValues(dirtyFields, data);
        onSubmit(dirtyData, additional);
      } else {
        const inValidFields = getInValidPostFields({
          currentFormSchema: formSchema,
          initialValues,
        });

        // assign null to all empty string fields
        const currentPostData = Object.fromEntries(
          Object.entries(data).map(([key, value]) =>
            value === '' ? [key, null] : [key, value],
          ),
        );

        // Remove all fields with register false, or readOnly
        // This is for maintenance pages schema
        inValidFields.forEach(vf => {
          if (Object.prototype.hasOwnProperty.call(currentPostData, vf)) {
            delete currentPostData[vf];
          }
        });

        onSubmit(currentPostData, additional);
      }
    };

    useImperativeHandle(ref, () => ({
      submit: methods.handleSubmit(onLocalSubmit),
      reset: methods.reset,
      resetAndNavigate: url => {
        methods.reset(initialValues);
        setTimeout(() => navigate(url), 0);
      },
      isFormDirty: () => methods.formState.isDirty,
      getValues: () => methods.getValues(),
      setError: methods.setError,
    }));

    unstable_usePrompt({
      when: !disableUnSavedCheck && methods.formState.isDirty,
      message: 'You have unsaved changes. Press Cancel to stay on page.',
    });

    useEffect(() => {
      methods.reset(initialValues);
    }, [initialValues, methods, isSearchForm]);

    return (
      <FormProvider {...methods}>
        <form
          ref={ref}
          autoComplete="off"
          onSubmit={onSubmit ? methods.handleSubmit(onLocalSubmit) : undefined}>
          <FormErrors errors={errors} />
          <FormWarnings errors={warnings} />
          <FormStateErrors errors={methods.formState.errors} />
          {children}
          {onReadyRenderFormItems()}
        </form>
      </FormProvider>
    );
  },
);

export default observer(FormHandler);
